import { z } from 'zod';

/**
 * Any other arbitraty data the webhook may supply that we need to store.
 * We _may_ want to extend on this per provider?
 */
export const WebhookBaseMetadataSchema = z
  .object({
    /** External name, if any */
    name: z.string().nullable(),
    /** Which event/s are we subscribed to on this webhook? */
    events: z.array(z.string()),
    /**
     * Query/filters/conditions we may want to set to limit the data we receive from external service
     * E.g, for JIRA webhooks, we can filter by project, issue type, etc.
     * @example {"fieldIdsFilter": "summary", "jqlFilter": "project = PROJ" } from JIRA
     * @link https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-webhooks/#api-rest-api-3-webhook-post
     * @note We should try to make these Zod/typesafe per Provider, but for now, allow any object.
     */
    conditions: z.string().or(z.object({}).passthrough()).nullable(),
    /**
     * Some webhooks (e.g JIRA) need to be refreshed after a certain number of days.
     * This is usually returned from provider after install and stored to `expires_at` in the DB.
     * But in case that's not always true, we'll use this as a fallback.)
     *
     * Optional, as it may be confusing to have around vs the `expires_at` field.
     */
    expires_every_num_days: z.number().optional(),
  })
  .passthrough();
export type WebhookBaseMetadata = z.infer<typeof WebhookBaseMetadataSchema>;

/** Shared definitions across the configurable/provider definition, write+read from DB */
const WebhookBaseSchema = z.object({
  /** Per-subscription unique identifier for the webhook, deterministic */
  webhook_key: z.string(),
  /** Metadata and config for this webhook, including events we subscibe to, query filters, etc. */
  metadata: WebhookBaseMetadataSchema,
  /**
   * Where host/path this endpoint hit the TIC infrastructure?
   * Allows both {webhook_base_url}-placeholders and real URLs.
   * @note Do we need to allow ?query=params at Provider/Transformer level?
   *       If so, ensure that we correctly append additional query params
   *       defined in the `syncWebhooks` operation.
   */
  destination_url: z
    .string()
    // .url() // Due to placeholders, we can't use this
    .refine(
      url => {
        // We'll likely store this and inject URL at install-time
        if (url.includes('{webhook_base_url}/')) return true;

        // But if not, this rule should ensure we only point to correct destination
        // This validation is most important in the actual performing of installs, so maybe it should live elsewhere?
        const { hostname } = new URL(url);
        const allowedDestinations = [
          'webhooks.integrations.timelyapp.com',
          'webhooks.integrations.betimely.com',
          'webhooks.development.timelyapp.com',
        ];

        return allowedDestinations.includes(hostname);
      },
      {
        message:
          'Invalid destination URL. Hostname must be in the allow list, and paths must be prefixed with /:version/webhooks/:service',
      }
    ),
});

/**
 * Webhook "install definition/configuration", set in the providers.
 * These instructions will execute, and must return a `WebhookWriteSchema`
 * that will be stored in the TIC database.
 */
export const WebhookInstallDefinitionSchema = WebhookBaseSchema;
export type WebhookInstallDefinition = z.infer<typeof WebhookInstallDefinitionSchema>;

/**
 * The per-Provider configuration of webhooks shouldn't need to worry about
 * determining active/active state (e.g install or uninstall operation),
 * nor should it be responsible for setting self_api_url, etc.
 * @note replaced 'CreateWebhookConfig
 */
export const ProviderWebhookConfigSchema = z.object({
  url: z.string(),
  method: z.enum(['POST', 'PUT', 'DELETE']),
  /** If provider let's us set the secret.. */
  secret: z.string().optional(),
  payload: z.object({}).passthrough().optional(),
  idField: z
    .string()
    .nullable()
    .describe(
      'Path to the webhook ID in the payload, e.g "webhook.id". If not found, it will double-check on the `data` object if returned.'
    ),
  secretField: z.string().optional(),
  validateSuccess: z.function(z.tuple([z.object({})])).optional(),
  expiresAt: z.string().optional(),
  // secretHeaderKey, expiresAtField, etc, etc?
});

export type ProviderWebhookConfig = z.infer<typeof ProviderWebhookConfigSchema>;

/**
 * Webhook "write" schema, updated after successful install and webhook hits/updates.
 * @note Properties that seeminly belong on BaseSchema are added here (which `Read` schema inherits from).
 *       This is to ensure that `WebhookInstallDefinitionSchema` remains unaware of DB-specifics,
 *
 * @note This was relaxed/more optional-ity added to allow for partial updates. Forcing `null`
 *       made it significantly more annoying to attempt partial updates.
 */
export const WebhookWriteSchema = WebhookBaseSchema.extend({
  id: z.string().uuid().optional(),
  /** The subscription this webhook belongs to */
  subscription_id: z.string().uuid(),
  /** Secret is _usually_ received after install, but some support defining this up-front */
  secret: z.string().nullable().optional(),
  /** Is this webhook currently active? */
  active: z.boolean().describe('Whether the webhook subscription is currently active'),
  installed_at: z.string().datetime({ offset: true }).nullable(),
  /** Date of uninstall/disable, manually or as result of webhook failures */
  uninstalled_at: z.string().datetime({ offset: true }).nullable(),
  /** In the event of webhooks expiring after a certain duration, set this to date-of-expiration minus a few days as buffer */
  expires_at: z.string().datetime({ offset: true }).nullable(),
  /** The external identifier of this webhook */
  external_id: z.coerce.string().nullable(),
  /** When did webhook last (successfully) hit? */
  last_success_at: z.string().datetime({ offset: true }).nullable(),
  /** When did webhook last fail? */
  last_failure_at: z.string().datetime({ offset: true }).nullable(),
  /** What was the last failure reason? */
  last_failure_reason: z.string().nullable(),
  /** JSON of last payload  */
  last_recieved_payload: z.object({}).passthrough().nullable(),
  updated_at: z.string().datetime({ offset: true }),
  /**
   * The payload we received back from the external service when we installed or updated the webhook.
   * @note consider rename when everything works, e.g `crupdate_payload` to indicate it's the last "create/update of webhook" payload.
   */
  crupdated_payload: z.object({}).passthrough().nullable().optional(),
});

export type WebhookWrite = z.infer<typeof WebhookWriteSchema>;

/**
 * Schema for partial updates of subscribed webhook
 */
export const WebhookUpdateSchema = WebhookWriteSchema.partial().required({
  id: true,
  subscription_id: true,
  webhook_key: true,
  active: true,
  updated_at: true,
});
export type WebhookUpdate = z.infer<typeof WebhookUpdateSchema>;

/**
 * Webhook "read" schema, as returned from via TIC db/API calls.
 * @note Extends the `WebhookWriteSchema` (which extends on the base schema)
 */
export const WebhookReadSchema = WebhookWriteSchema.extend({
  id: z.string().uuid(),
  /** For "prepared but not installed" webhooks, we don't know this yet. */
  external_id: z.coerce.string().nullable(),
  /**
   * When was this webhook (re)installed?
   * Some services like JIRA will use this to determine if an installed webhook needs to be re-installed/re-verified
   * @link https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-webhooks/#api-rest-api-3-webhook-refresh-put
   */
  installed_at: z.string().datetime({ offset: true }).nullable(),
  /** When was this record last updated */
  updated_at: z.string().datetime({ offset: true }),
});

export type WebhookRead = z.infer<typeof WebhookReadSchema>;

// 1 subscription active=false
// webhook c
